import { Alert, Button, Empty, Spin, Tag } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { get } from '../../../../axios/axios';
import {
    beginPendingPpt,
    getPendingPptEntitlement,
} from '../../../../components/Order/pptEntitlement';
import styles from './DownloadOrderModal.module.scss';

export const DownloadOrderModal = ({
    orderNo,
    fallbackOrderNo,
    queryType = 'query',
    queryPath,
}) => {
    const [loading, setLoading] = useState(false);
    const [info, setInfo] = useState(null);
    const [error, setError] = useState('');
    const [refresh, setRefresh] = useState(0);
    const endpoint = queryPath || `/ai/ai/paper/${queryType === 'query2' ? 'query2' : 'query'}`;

    useEffect(() => {
        setInfo(null);
    }, [orderNo, endpoint]);
    useEffect(() => {
        let cancelled = false;
        let timer;
        const controller = new AbortController();
        const scheduleRefresh = () => {
            timer = setTimeout(() => {
                if (!cancelled) setRefresh((value) => value + 1);
            }, 30000);
        };
        const fetchOrder = async () => {
            if (!orderNo) {
                setError('缺少订单凭证，请返回套餐列表重新打开。');
                return;
            }
            setLoading(true);
            setError('');
            try {
                const queryCredential = async (credential) => {
                    const response = await get(
                        endpoint,
                        { orderNo: credential },
                        { signal: controller.signal, slient: true },
                    );
                    if (response.success === false || !response.data)
                        throw new Error(response.msg || '暂未查询到生成结果，请稍后刷新。');
                    return response;
                };
                let res;
                try {
                    res = await queryCredential(orderNo);
                } catch (err) {
                    // 历史 PPT 和降重服务按兑换码作为交易流水查询。
                    const detail = err.response?.data?.msg || err.message || '';
                    if (
                        fallbackOrderNo &&
                        fallbackOrderNo !== orderNo &&
                        /未查询到|未找到|不存在/.test(detail)
                    ) {
                        res = await queryCredential(fallbackOrderNo);
                    } else {
                        throw err;
                    }
                }
                if (cancelled) return;
                const result = {
                    ...res.data,
                    list: Array.isArray(res.data.list) ? res.data.list : [],
                };
                setInfo(result);
                if (!result.list.length || result.list.some((item) => !item.downloadUrl))
                    scheduleRefresh();
            } catch (err) {
                if (cancelled) return;
                const detail = err.response?.data?.msg || err.message || '查询失败，请刷新重试。';
                setError(detail);
                if (/生成中|处理中|稍后/.test(detail)) scheduleRefresh();
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchOrder();
        return () => {
            cancelled = true;
            clearTimeout(timer);
            controller.abort();
        };
    }, [endpoint, orderNo, fallbackOrderNo, refresh]);

    const files = info?.list || [];
    const generatedFiles = files.filter((item) => !getPendingPptEntitlement(item.downloadUrl));
    const completed = generatedFiles.filter((item) => item.downloadUrl).length;
    const pending = !files.length || completed < generatedFiles.length;
    const createPpt = (entitlement) => {
        try {
            beginPendingPpt(entitlement);
        } catch (err) {
            setError(err.message || '暂时无法进入PPT制作，请重试');
        }
    };
    return (
        <div className={styles.orderModal}>
            <p className={styles.intro}>
                文件生成完成后，点击对应的“下载文件”即可保存。尚未完成的文件会显示“生成中”。
            </p>
            <div className={styles.toolbar}>
                <span>订单凭证：{orderNo}</span>
                <Button
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={() => setRefresh((value) => value + 1)}
                >
                    刷新结果
                </Button>
            </div>
            {error && (
                <Alert
                    type={/生成中|处理中/.test(error) ? 'info' : 'warning'}
                    showIcon
                    message={error}
                />
            )}
            <Spin spinning={loading}>
                {info ? (
                    <>
                        <div className={styles.heading}>
                            <h3>{info.paperTitle || '我的生成文件'}</h3>
                            {info.createTime && <span>{info.createTime}</span>}
                        </div>
                        {pending && (
                            <Alert
                                showIcon
                                type="info"
                                message={
                                    files.length
                                        ? `已生成 ${completed} / ${generatedFiles.length} 个文件，其余文件生成中`
                                        : '正在准备生成结果'
                                }
                                description="已完成的文件可先下载，页面会自动更新，也可点击“刷新结果”。"
                            />
                        )}
                        <div className={styles.files}>
                            {files.map((file, index) => {
                                const pptEntitlement = getPendingPptEntitlement(file.downloadUrl);
                                return (
                                    <div className={styles.file} key={`${file.title}-${index}`}>
                                        <div className={styles.fileName}>
                                            <strong>
                                                {pptEntitlement
                                                    ? '答辩PPT'
                                                    : file.title || `文件 ${index + 1}`}
                                            </strong>
                                            <Tag
                                                color={
                                                    pptEntitlement
                                                        ? 'gold'
                                                        : file.downloadUrl
                                                          ? 'green'
                                                          : 'processing'
                                                }
                                            >
                                                {pptEntitlement
                                                    ? '待制作'
                                                    : file.downloadUrl
                                                      ? '可下载'
                                                      : '生成中'}
                                            </Tag>
                                        </div>
                                        {pptEntitlement ? (
                                            <Button
                                                type="primary"
                                                onClick={() => createPpt(pptEntitlement)}
                                            >
                                                制作答辩PPT
                                            </Button>
                                        ) : (
                                            <Button
                                                type={file.downloadUrl ? 'primary' : 'default'}
                                                icon={
                                                    file.downloadUrl ? <DownloadOutlined /> : null
                                                }
                                                disabled={!file.downloadUrl}
                                                href={file.downloadUrl || undefined}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {file.downloadUrl ? '下载文件' : '生成中，请稍候'}
                                            </Button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                ) : !loading && !error ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="暂未查询到文件，请刷新结果"
                    />
                ) : null}
            </Spin>
        </div>
    );
};
